import { Links, Meta, Scripts, ScrollRestoration } from 'react-router';

import './app.css';
import './styles/neon-background.css';

import { useTheme } from './hooks/use-theme';
import './lib/i18n';
import { SentryErrorBoundary } from './lib/sentry';

import { I18nProvider } from './i18n-provider';
import { AuthProvider } from './auth/contexts/auth-context';
import { SearchProvider } from '~/contexts/search-context';

import CookieConsent from '~/components/cookie-consent'; // Import CookieConsent
import { AppLayout } from '~/components/layout/app-layout';
import { SentryFallback } from '~/components/error-boundaries/sentry-fallback';
import { RootErrorBoundary } from '~/components/error-boundaries/root-error-boundary';
import { meta } from './root.meta';
import { links } from './root.links';
import { loader } from './root.loader';
import { isGaEnabled, gaMeasurementId } from './config/ga'; // Re-import GA config

export { meta, links, loader };
export { RootErrorBoundary as ErrorBoundary };

export default function App() {
  const { theme } = useTheme();

  return (
    <html lang="en" className={theme} suppressHydrationWarning>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
        {/* Google Consent Mode Default Settings */}
        {isGaEnabled && gaMeasurementId && (
          <script
            dangerouslySetInnerHTML={{
              __html: `
                window.dataLayer = window.dataLayer || [];
                function gtag(){dataLayer.push(arguments);}
                gtag('consent', 'default', {
                  'analytics_storage': 'denied',
                  'ad_storage': 'denied',
                  'ad_user_data': 'denied',
                  'ad_personalization': 'denied'
                });
              `,
            }}
          />
        )}
        {/* Google Analytics Scripts - Load unconditionally */}
        {isGaEnabled && gaMeasurementId && (
          <>
            <script
              async
              src={`https://www.googletagmanager.com/gtag/js?id=${gaMeasurementId}`}
            />
            <script
              dangerouslySetInnerHTML={{
                __html: `
                  window.dataLayer = window.dataLayer || [];
                  function gtag(){dataLayer.push(arguments);}
                  gtag('js', new Date());
                  gtag('config', '${gaMeasurementId}');
                `,
              }}
            />
          </>
        )}
      </head>
      <body
        className={`relative flex min-h-screen flex-col ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}
        style={{ fontFamily: "'Poppins', sans-serif" }}
      >
        <SentryErrorBoundary fallback={SentryFallback}>
          <I18nProvider>
            <AuthProvider>
              <SearchProvider>
                <AppLayout />
              </SearchProvider>
              <CookieConsent />
            </AuthProvider>
          </I18nProvider>
        </SentryErrorBoundary>
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}
